import { useEffect, useRef, useCallback } from "react";

/**
 * Hook para la aparición suave y repetible de secciones (Scroll Reveal)
 * - IntersectionObserver bidireccional: anima al bajar y se reinicia al subir.
 * - Al salir por abajo al hacer scroll hacia arriba, se remueve "PrincipalFx-reveal--visible"
 *   para que al volver a bajar se vuelva a apreciar el efecto.
 * - Secciones ya visibles al cargar se muestran de inmediato.
 * - Con prefers-reduced-motion o sin soporte de IntersectionObserver, no oculta nada.
 */
export default function useRevealOnScroll() {
  const observerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const registerReveal = useCallback((el) => {
    if (!el) return;
    if (el.dataset.principalfxRegistered) return;
    el.dataset.principalfxRegistered = "true";

    // 1. Respetar prefers-reduced-motion
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    // 2. Si no hay soporte para IntersectionObserver, mostrar todo sin animación
    if (typeof IntersectionObserver === "undefined") {
      return;
    }

    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("PrincipalFx-reveal--visible");
            } else {
              // Solo si el elemento sale completamente por la parte inferior de la pantalla (al subir scroll),
              // se remueve la clase visible para que al volver a bajar se reactive la animación.
              const windowHeight =
                window.innerHeight || document.documentElement.clientHeight || 0;
              const rect = entry.target.getBoundingClientRect();
              if (rect.top >= windowHeight) {
                entry.target.classList.remove("PrincipalFx-reveal--visible");
              }
            }
          });
        },
        {
          threshold: 0.02,
          rootMargin: "0px 0px 40px 0px",
        }
      );
    }

    // Aplicar clase base de revelado desde JS
    el.classList.add("PrincipalFx-reveal");

    // Verificar si ya está visible en el viewport al renderizarse
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight || 0;
    const rect = el.getBoundingClientRect();
    const isAlreadyVisible = rect.top < windowHeight - 40 && rect.bottom > 0;

    if (isAlreadyVisible) {
      el.classList.add("PrincipalFx-reveal--visible");
    }

    // Observar continuamente para que re-anime al subir y bajar
    observerRef.current.observe(el);
  }, []);

  return registerReveal;
}
